import { Component, OnDestroy, OnInit, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { Subscription, interval, forkJoin, of } from 'rxjs';
import { switchMap, takeWhile, skip, catchError, map } from 'rxjs/operators';

import { SidebarComponent } from '../../common/slidebar/sidebar.components';
import { HeaderComponent } from '../../common/header/header.component';
import { RestrictedAccessWarningDirective } from '../../../directives/restricted-access-warning.directive';
import { SearchServiceStatusService } from '../../../services/search-service-status/search-service-status.service';
import { BasicSearchService } from '../../../services/basic-search/basic-search.service';
import { UserAccountService } from '../../../services/user-account/user-account.service';
import { CountriesService } from '../../../services/countries/countries.service';
import { UtilsService } from '../../../services/utils/utils.service';
import { DocumentExportService } from '../../../services/document-export/document-export.service';
import { SearchPollingService } from '../../../services/search-polling/search-polling.service';
import {
  getSearchVendorStatusLabel,
  isBackgroundRefreshPending,
  isVendorSyncComplete,
} from '../../../services/search-polling/osint-job-poll-response.util';
import { CreditsLabelPipe } from '../../../pipes/credits-label.pipe';

@Component({
  selector: 'app-basic-search',
  standalone: true,
  imports: [CommonModule,
    FormsModule,
    SidebarComponent,
    RestrictedAccessWarningDirective,
    HeaderComponent, CreditsLabelPipe],
  templateUrl: './basic-search.component.html',
  styleUrl: './basic-search.component.css',
})
export class BasicSearchComponent implements OnInit, OnDestroy {
  availableCredits: number | null = null;
  creditsLoading = true;
  totalCredits: number = 0;

  singleNumber: string = '';
  textAreaInput: string = '';
  multipleNumbers: string[] = [];
  readonly maxNumberRows = 5;

  selectedCountryCode: string = '+91';
  isMultipleSearch: boolean = false;
  countries: any[] = [];
  selectedCountry: any = null;
  showCountryDropdown: boolean = false;
  countrySearchTerm: string = '';
  filteredCountries: any[] = [];

  loadingJobIds: boolean = false;
  loadingRecentResults: boolean = false;
  jobIds: string[] = [];

  private lastSearchTime: number = 0;
  private readonly SEARCH_THROTTLE_MS: number = 1000;
  private pollSub?: Subscription;
  private allJobResultsSubscription?: Subscription;

  searchType: string = 'phone';
  recentResults: any[] = [];
  recentResultsPaged: any[] = [];

  ipAddress: string = '';
  location: string = '';

  recentResultsCount: number = 0;
  recentResultsPerPage = 5;
  recentResultsCurrentPage = 1;
  recentResultsHasPreviousPage: boolean = false;
  recentResultsHasNextPage: boolean = false;

  recentResultsSelectAll = { isSelected: false };
  searchCreditCost: number | null = null;
  isServiceDisabled = false;

  selectedFilterType: string = 'number';
  filterTypes: any = [
    { label: 'Number', value: 'number' },
    { label: 'Notes', value: 'notes' },
    { label: 'Date Added', value: 'date-added' },
  ];

  filterKey: string = '';
  filterValue: string = '';
  numberFilter: string = '';
  notesFilter: string = '';
  startDateFilter: string = '';
  endDateFilter: string = '';

  get recentResultsPagedStartAndEnd() {
    if (!this.recentResults?.length || !this.recentResultsCount) {
      return { start: 0, end: 0 };
    }
    const start =
      (this.recentResultsCurrentPage - 1) * this.recentResultsPerPage + 1;
    const end = Math.min(
      start + this.recentResults.length - 1,
      this.recentResultsCount,
    );
    return { start, end };
  }

  get selectedRecentResults() {
    return this.recentResultsPaged
      .filter((r: any) => r.isSelected)
      .map((r: any) => r.basic_search_id || r.osint_search_id);
  }

  constructor(
    private router: Router,
    private toastr: ToastrService,
    private searchServiceStatusService: SearchServiceStatusService,
    private basicSearchService: BasicSearchService,
    private userAccountService: UserAccountService,
    public countriesService: CountriesService,
    private utilsService: UtilsService,
    private documentExportService: DocumentExportService,
    private searchPollingService: SearchPollingService,
  ) {}

  ngOnInit() {
    this.searchServiceStatusService.creditCosts$.subscribe((cc) => {
      this.searchCreditCost = this.searchServiceStatusService.resolveCreditCost(
        cc,
        'basicSearchCreditCost',
      );
    });
    this.searchServiceStatusService.searchServices$.subscribe((services) => {
      if (services) {
        this.isServiceDisabled = services.basic === false;
      }
    });
    this.loadCredits();
    this.initializeCountries();
    this.subscribeToJobResultsUpdates();
    this.getRecentResults();
  }

  ngOnDestroy() {
    this.pollSub?.unsubscribe();
    this.allJobResultsSubscription?.unsubscribe();
  }

  subscribeToJobResultsUpdates() {
    this.allJobResultsSubscription?.unsubscribe();
    this.allJobResultsSubscription = this.searchPollingService
      .getAllJobResults()
      .pipe(skip(1))
      .subscribe((map) => {
        let completedAny = false;
        this.recentResultsPaged.forEach((result: any) => {
          const id = String(result.basic_search_id || result.osint_search_id || '');
          if (!id) return;
          const updatedStatus = map.get(id);
          if (!updatedStatus) return;

          const wasComplete = isVendorSyncComplete(result);
          result.done = Boolean(updatedStatus.done);
          result.background_refresh_pending = Boolean(
            updatedStatus.background_refresh_pending,
          );
          result.status =
            updatedStatus.status ||
            (isVendorSyncComplete(result) ? 'completed' : 'pending');
          result.progress_percent =
            updatedStatus.progress_percent ?? result.progress_percent ?? null;
          result.progress = updatedStatus.progress ?? result.progress ?? null;
          result.vendor_sync_complete = isVendorSyncComplete({
            ...result,
            ...updatedStatus,
          });

          if (!wasComplete && isVendorSyncComplete(result)) {
            completedAny = true;
          }
        });

        if (completedAny) {
          this.getRecentResults();
          this.loadCredits();
        }
      });
  }

  loadCredits() {
    this.creditsLoading = true;
    try {
      const raw = localStorage.getItem('USER_PORTAL');
      if (raw) {
        const portal = JSON.parse(raw);
        const sub = portal?.subscriptionDetails || {};
        const cached =
          sub.available_credits ?? sub.remaining_credits ?? null;
        if (cached != null) {
          this.availableCredits = cached;
        }
        this.totalCredits = sub.total_credits ?? 0;
      }
    } catch {
      /* ignore */
    }
    this.userAccountService.getUserProfile().subscribe({
      next: (res: any) => {
        const sub = res?.data?.subscriptions?.[0] || res?.data?.subscription || {};
        this.availableCredits = this.userAccountService.pickAvailableCredits(res);
        if (sub?.total_credits != null) {
          this.totalCredits = sub.total_credits;
        }
        this.creditsLoading = false;
      },
      error: () => {
        this.availableCredits = this.availableCredits ?? 0;
        this.creditsLoading = false;
      },
    });
  }

  initializeCountries() {
    const list = this.countriesService.getAllCountries() || [];
    if (list.length) {
      this.countries = list.map((c: any) => ({
        code: c.code || c.iso2 || c.countryCode,
        name: c.name,
        dialCode: c.dialCode || c.dial_code || c.phoneCode,
      }));
    } else {
      this.countries = [
        { code: 'IN', name: 'India', dialCode: '+91' },
        { code: 'US', name: 'United States', dialCode: '+1' },
        { code: 'GB', name: 'United Kingdom', dialCode: '+44' },
      ];
    }
    this.filteredCountries = this.countries;
    this.selectedCountry =
      this.countries.find((c) => c.dialCode === this.selectedCountryCode) ||
      this.countries[0];
    this.selectedCountryCode = this.selectedCountry?.dialCode || '+91';
  }

  toggleCountryDropdown() {
    this.showCountryDropdown = !this.showCountryDropdown;
    if (this.showCountryDropdown) {
      this.countrySearchTerm = '';
      this.filterCountries();
    }
  }

  selectCountry(country: any) {
    this.selectedCountry = country;
    this.selectedCountryCode = country.dialCode;
    this.showCountryDropdown = false;
    this.countrySearchTerm = '';
    this.filterCountries();
  }

  filterCountries() {
    if (!this.countrySearchTerm.trim()) {
      this.filteredCountries = this.countries;
      return;
    }
    const term = this.countrySearchTerm.toLowerCase().trim();
    this.filteredCountries = this.countries.filter(
      (c) =>
        c.name.toLowerCase().includes(term) ||
        c.dialCode.includes(this.countrySearchTerm.trim()) ||
        c.code.toLowerCase().includes(term),
    );
  }

  onCountrySearchChange() {
    this.filterCountries();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event) {
    const target = event.target as HTMLElement;
    if (
      !target.closest('.country-select-wrapper') &&
      !target.closest('.country-dropdown')
    ) {
      this.showCountryDropdown = false;
    }
  }

  getFlagImageUrl(countryCode: string): string {
    return `https://flagcdn.com/w40/${(countryCode || 'in').toLowerCase()}.png`;
  }

  onFilterTypeChange() {
    this.numberFilter = '';
    this.notesFilter = '';
    this.startDateFilter = '';
    this.endDateFilter = '';
    this.filterKey = '';
    this.filterValue = '';
    this.recentResultsCurrentPage = 1;
    this.getRecentResults();
  }

  isThrottled(): boolean {
    const now = Date.now();
    if (now - this.lastSearchTime < this.SEARCH_THROTTLE_MS) {
      this.toastr.info('Please wait before searching again');
      return true;
    }
    this.lastSearchTime = now;
    return false;
  }

  onMultipleSearchChange(): void {
    this.showCountryDropdown = false;
    if (this.isMultipleSearch) {
      if (!this.textAreaInput.trim() && this.singleNumber.trim()) {
        this.textAreaInput = this.singleNumber.trim();
      }
      return;
    }
    if (!this.singleNumber.trim()) {
      const first = this.textAreaInput
        .split('\n')
        .map((value) => value.trim())
        .find((value) => value.length > 0);
      if (first) {
        this.singleNumber = first;
      }
    }
  }

  onSearchSingleNumberButtonClick() {
    if (this.isServiceDisabled) {
      this.toastr.error('Basic search is currently unavailable');
      return;
    }

    const phones = this.collectPhones();
    if (!phones.length) {
      this.toastr.error(
        this.isMultipleSearch
          ? 'Please enter at least one phone number'
          : 'Please enter a phone number',
      );
      return;
    }

    if (phones.length > this.maxNumberRows) {
      this.toastr.error(
        `You can only search a maximum of ${this.maxNumberRows} numbers at once.`,
      );
      return;
    }

    if (this.isThrottled()) return;

    this.isMultipleSearch = phones.length > 1;
    this.startSearch(phones);
  }

  onSearchMultipleNumbersButtonClick() {
    this.isMultipleSearch = true;
    this.onSearchSingleNumberButtonClick();
  }

  private collectPhones(): string[] {
    const rawValues = this.isMultipleSearch
      ? this.textAreaInput.split('\n')
      : [this.singleNumber];

    return rawValues
      .map((value) => String(value || '').trim().replace(/\s+/g, ''))
      .filter((value) => value.length > 0)
      .map((n) => (n.startsWith('+') ? n : `${this.selectedCountryCode}${n}`));
  }

  extractMultipleNumbers() {
    this.multipleNumbers = this.collectPhones().slice(0, this.maxNumberRows);
  }

  startSearch(phones: string[]) {
    this.loadingJobIds = true;
    this.basicSearchService.startSearch(phones).subscribe({
      next: (response) => {
        this.loadingJobIds = false;
        const searches = response?.data?.searches || [];
        if (!searches.length) {
          this.toastr.warning('No search jobs created');
          return;
        }
        this.toastr.success(
          searches.length > 1
            ? `Basic search started for ${searches.length} numbers`
            : 'Basic search started',
        );
        this.singleNumber = '';
        this.textAreaInput = '';
        this.multipleNumbers = [];
        this.isMultipleSearch = false;
        this.loadCredits();
        this.getRecentResults();
        this.searchPollingService.ensureBackgroundPolling(
          searches.map((s: any) => ({
            job_id: String(s.basic_search_id),
            searchType: 'basic',
            label: String(s.searched_phone || s.basic_search_id),
          })),
          2500,
        );
        this.pollPending(searches.map((s: any) => s.basic_search_id));
      },
      error: (error) => {
        this.loadingJobIds = false;
        const msg =
          error?.error?.message_key ||
          error?.error?.message ||
          error?.message ||
          'Search failed';
        this.toastr.error(msg, 'Search Failed');
      },
    });
  }

  pollPending(ids: string[]) {
    this.pollSub?.unsubscribe();
    if (!ids.length) return;
    this.pollSub = interval(2500)
      .pipe(
        switchMap(() =>
          forkJoin(
            ids.map((id) =>
              this.basicSearchService.getStatus(id).pipe(
                catchError(() =>
                  of({
                    data: {
                      basic_search_id: id,
                      done: false,
                      background_refresh_pending: false,
                    },
                  }),
                ),
              ),
            ),
          ),
        ),
        map((responses) =>
          (responses || []).map((res: any) => ({
            id: String(res?.data?.basic_search_id || ''),
            done: Boolean(res?.data?.done),
            background_refresh_pending: Boolean(
              res?.data?.background_refresh_pending,
            ),
            vendor_sync_complete: isVendorSyncComplete(res?.data),
          })),
        ),
        takeWhile(
          (statuses) => statuses.some((s) => !s.vendor_sync_complete),
          true,
        ),
      )
      .subscribe({
        next: () => this.getRecentResults(),
        error: () => undefined,
      });
  }

  isRecentRowComplete(row: any): boolean {
    return isVendorSyncComplete(row);
  }

  isRecentRowRefreshing(row: any): boolean {
    return Boolean(row?.done) && isBackgroundRefreshPending(row);
  }

  getRecentRowStatusLabel(row: any): string {
    return getSearchVendorStatusLabel(row);
  }

  getRecentResults() {
    this.loadingRecentResults = true;
    const params: any = {
      page: this.recentResultsCurrentPage,
      limit: this.recentResultsPerPage,
    };
    if (this.filterKey === 'searched_phone' && this.filterValue) {
      params.search_key = 'searched_phone';
      params.search_value = this.filterValue;
    }
    if (this.filterKey === 'notes' && this.filterValue) {
      params.search_key = 'notes';
      params.search_value = this.filterValue;
    }
    if (this.filterKey === 'created_on' && this.startDateFilter && this.endDateFilter) {
      params.search_key = 'created_on';
      params.start_date = this.startDateFilter;
      params.end_date = this.endDateFilter;
    }

    this.basicSearchService.getHistory(params).subscribe({
      next: (response) => {
        const list = response?.data?.data_list || response?.data || [];
        this.recentResults = (Array.isArray(list) ? list : []).map((r: any) => ({
          ...r,
          osint_search_id: r.basic_search_id,
          isSelected: false,
        }));
        this.recentResultsCount = response?.data?.total_count ?? this.recentResults.length;
        this.recentResultsPaged = this.recentResults;
        this.recentResultsHasPreviousPage = this.recentResultsCurrentPage > 1;
        this.recentResultsHasNextPage =
          this.recentResultsCurrentPage * this.recentResultsPerPage <
          this.recentResultsCount;
        this.loadingRecentResults = false;
        this.recentResultsSelectAll.isSelected = false;

        const pending = this.recentResults.filter(
          (r: any) => !isVendorSyncComplete(r),
        );
        if (pending.length) {
          this.searchPollingService.ensureBackgroundPolling(
            pending.map((r: any) => ({
              job_id: String(r.basic_search_id || r.osint_search_id),
              searchType: 'basic',
              label: String(r.searched_phone || r.basic_search_id),
            })),
            2500,
          );
        }
      },
      error: () => {
        this.loadingRecentResults = false;
        this.toastr.error('Failed to load recent searches');
      },
    });
  }

  onRecentResultsPageChange(page: number) {
    this.recentResultsCurrentPage = page;
    this.getRecentResults();
  }

  onRecentResultsSelectAllClick() {
    this.recentResultsSelectAll.isSelected =
      !this.recentResultsSelectAll.isSelected;
    this.recentResultsPaged = this.recentResultsPaged.map((r: any) => ({
      ...r,
      isSelected: this.recentResultsSelectAll.isSelected,
    }));
  }

  onRecentResultPagedClick(event: Event, recentResultPaged: any) {
    event.stopPropagation();
    recentResultPaged.isSelected = !recentResultPaged.isSelected;
    this.recentResultsSelectAll.isSelected = this.recentResultsPaged.every(
      (r: any) => r.isSelected,
    );
  }

  onPhoneFilterChange(phone: string) {
    const trimmed = phone.trim();
    if (trimmed === '') {
      this.filterKey = '';
      this.filterValue = '';
    } else if (trimmed.length === 1) {
      return;
    } else {
      this.filterKey = 'searched_phone';
      this.filterValue = trimmed;
    }
    this.recentResultsCurrentPage = 1;
    this.getRecentResults();
  }

  onNotesFilterChange(notes: string) {
    const trimmed = notes.trim();
    if (trimmed === '') {
      this.filterKey = '';
      this.filterValue = '';
    } else if (trimmed.length === 1) {
      return;
    } else {
      this.filterKey = 'notes';
      this.filterValue = trimmed;
    }
    this.recentResultsCurrentPage = 1;
    this.getRecentResults();
  }

  onStartDateFilterChange(startDate: string) {
    this.startDateFilter = startDate;
    if (this.startDateFilter && this.endDateFilter) {
      this.filterKey = 'created_on';
      this.recentResultsCurrentPage = 1;
      this.getRecentResults();
    }
  }

  onEndDateFilterChange(endDate: string) {
    this.endDateFilter = endDate;
    if (this.startDateFilter && this.endDateFilter) {
      this.filterKey = 'created_on';
      this.recentResultsCurrentPage = 1;
      this.getRecentResults();
    }
  }

  applyFilters() {
    this.getRecentResults();
  }

  onExportExcelButtonClick() {
    const rows = this.transformRecentResultsPaged();
    if (!rows.length) {
      this.toastr.info('No recent results to export');
      return;
    }
    const timestamp = this.utilsService.formatDateTime(
      new Date().toISOString(),
      'dd-MM-yyyy_HH-mm-ss',
    );
    this.documentExportService.exportToExcel(
      rows,
      `basic-phone-results-${timestamp || 'export'}`,
      'Basic Search Results',
    );
  }

  onExportCsvButtonClick() {
    const rows = this.transformRecentResultsPaged();
    if (!rows.length) {
      this.toastr.info('No recent results to export');
      return;
    }
    const timestamp = this.utilsService.formatDateTime(
      new Date().toISOString(),
      'dd-MM-yyyy_HH-mm-ss',
    );
    this.documentExportService.exportToCsv(
      rows,
      `basic-phone-results-${timestamp || 'export'}`,
      'Basic Search Results',
    );
  }

  onExportPdfButtonClick() {
    const rows = this.transformRecentResultsPaged();
    if (!rows.length) {
      this.toastr.info('No recent results to export');
      return;
    }
    const timestamp = this.utilsService.formatDateTime(
      new Date().toISOString(),
      'dd-MM-yyyy_HH-mm-ss',
    );
    this.documentExportService.exportResultsToPdf(
      rows,
      `basic-phone-results-${timestamp || 'export'}`,
      'Basic search results generated from Ryno',
      this.getUsername(),
    );
  }

  private transformRecentResultsPaged() {
    return (this.recentResultsPaged || []).map((row: any) => ({
      'BASIC SEARCH ID': row.basic_search_id || row.osint_search_id || '',
      'JOB ID': row.job_id || '',
      'SEARCHED PHONE': row.searched_phone || '',
      STATUS: row.done ? 'Completed' : 'Pending',
      'USER EMAIL': row.user?.email || '',
      NOTES: row.notes === undefined ? null : row.notes,
      'CREATED ON': this.utilsService.formatDateTime(
        row.created_on,
        'MMM d, y, h:mm:ss a',
      ),
    }));
  }

  private getUsername(): string {
    try {
      const raw = localStorage.getItem('USER_PORTAL');
      const portal = raw ? JSON.parse(raw) : null;
      return portal?.loginDetails?.user_name || '';
    } catch {
      return '';
    }
  }

  onDeleteButtonClick() {
    const selected = this.selectedRecentResults;
    if (!selected.length) {
      this.toastr.warning('Please select at least one result to delete');
      return;
    }
    this.basicSearchService.deleteHistory(selected).subscribe({
      next: () => {
        this.searchPollingService.stopTrackingJobIds(
          selected.map((id: string) => String(id)),
        );
        this.toastr.success('Deleted');
        this.getRecentResults();
      },
      error: () => this.toastr.error('Delete failed'),
    });
  }

  goToSearchResultPage(searchResult: any) {
    const id = searchResult.basic_search_id || searchResult.osint_search_id;
    this.router.navigate(['/basic-search-result', id], {
      state: { searchResult },
    });
  }

  onBuyCreditsButtonClick() {
    this.router.navigate(['/plan-pricing']);
  }

  formatDate(value: string) {
    return this.utilsService?.formatDateTime?.(value) || value;
  }
}
